import { lazy, Suspense, forwardRef } from 'react'

// Cache for preloaded modules
const moduleCache = new Map()

/**
 * Replacement for loadable() to mimic API but use React.lazy + Suspense internally.
 * Now properly forwards refs to the loaded component and supports preloading.
 *
 * @param {Function} importFn - The dynamic import function.
 * @param {Object} options - Options object, supports { fallback }.
 * @returns {React.FC} - Wrapped component ready to use directly with ref support.
 */
export default function loadable(importFn, options = {}) {
	const { fallback } = options

	// Assign displayName for easier debugging
	const importMatch = importFn.toString().match(/['"](.+?)['"]/)
	const inferredName = importMatch ? importMatch[1].split('/').pop() : 'LoadableComponent'

	// Create cache key for this import
	const cacheKey = importFn.toString()

	// Wrap importFn to use cache
	const cachedImportFn = () => {
		if (moduleCache.has(cacheKey)) {
			return moduleCache.get(cacheKey)
		}

		const promise = loadWithRetry(importFn, { moduleName: inferredName }).then((module) => {
			// Replace promise with resolved module
			moduleCache.set(cacheKey, Promise.resolve(module))
			return module
		})

		moduleCache.set(cacheKey, promise)
		return promise
	}

	const LazyComponent = lazy(cachedImportFn)

	// Create the wrapper component with forwardRef to properly handle refs
	const Wrapped = forwardRef((props, ref) => {
		const UI = <LazyComponent {...props} ref={ref} />
		if (fallback) return <Suspense fallback={fallback}>{UI}</Suspense>
		return UI
	})

	Wrapped.displayName = `Loadable(${inferredName})`

	// Add preload method
	Wrapped.preload = () => {
		if (moduleCache.has(cacheKey)) {
			return moduleCache.get(cacheKey)
		}

		const promise = loadWithRetry(importFn, { moduleName: inferredName }).then((module) => {
			moduleCache.set(cacheKey, Promise.resolve(module))
			return module
		})

		moduleCache.set(cacheKey, promise)
		return promise
	}

	// Add load method (alias for preload)
	Wrapped.load = Wrapped.preload

	return Wrapped
}

function loadWithRetry(importFunc, { maxRetries = 3, retryDelay = 500, moduleName } = {}) {
	return new Promise((resolve, reject) => {
		const attempt = (n) => {
			importFunc()
				.then(resolve)
				.catch((error) => {
					if (n === 0) {
						console.error(`Module "${moduleName}" import failed after retries:`, error)
						reject(error)
						return
					}
					setTimeout(() => attempt(n - 1), retryDelay)
				})
		}
		attempt(maxRetries)
	})
}
